import { useEffect } from "react";
import { useNavigate } from "react-router";
import { useOrderLinkFlowOrder } from "../../../../hooks/api/v2/orderLinkFlow/useOrderLinkFlowOrder";
import * as ROUTES from "../../../../constants/routes";

interface BusinessUserAccessGuardProps {
  token: string | null;
  children: React.ReactNode;
  redirectTo?: string;
  skipUserCheck?: boolean;
}

/**
 * Route guard that redirects users to order summary if the business has active users.
 * This prevents access to delivery preferences, order editing, and other restricted flows.
 */
export function BusinessUserAccessGuard({
  token,
  children,
  redirectTo,
  skipUserCheck = false,
}: BusinessUserAccessGuardProps) {
  const navigate = useNavigate();
  const orderQuery = useOrderLinkFlowOrder(token);

  const redirectUrl = redirectTo || `${ROUTES.ORDER_SUMMARY}?token=${token}`;

  const retailerHasBusinessUser = orderQuery.data?.retailerBusinessUsers
    ? orderQuery.data.retailerBusinessUsers.length > 0
    : false;

  useEffect(() => {
    // Redirect if there's no token or if there's an error
    if (!token || orderQuery.error) {
      navigate(redirectUrl);
      return;
    }

    // Redirect if the business has active users (unless explicitly allowed)
    if (retailerHasBusinessUser && !skipUserCheck) {
      navigate(redirectUrl);
      return;
    }
  }, [
    token,
    orderQuery.error,
    navigate,
    redirectUrl,
    retailerHasBusinessUser,
    skipUserCheck,
  ]);

  // Show loading state while checking
  if (orderQuery.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-neutral-100">
        <div className="text-center">
          <div className="mb-4 h-8 w-8 animate-spin rounded-full border-2 border-zinc-300 border-t-zinc-800" />
          <p className="text-sm text-zinc-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Don't render children if there's an error or business has users (unless explicitly allowed)
  if (orderQuery.error || (retailerHasBusinessUser && !skipUserCheck)) {
    return null;
  }

  // Render children if access is allowed
  return <>{children}</>;
}
